/* eslint-disable no-unused-vars */

import { AUDIO_UPLOAD_EXTENSIONS } from '@/schema/chatbot-schema'

/**
 * Recorder containers in preference order.
 *
 * Browsers disagree on the default: Chromium records WebM/Opus, while WebKit (Safari, and every
 * iOS browser) records fragmented MP4/AAC unless asked otherwise. WebM comes first because every
 * backend version decodes it, and WebKit 18.4+ records it when requested. MP4 remains for older
 * WebKit, which cannot record anything else.
 */
export const PREFERRED_RECORDER_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'] as const

type RecorderSupport = { isTypeSupported: (_mimeType: string) => boolean }

/**
 * First entry of PREFERRED_RECORDER_MIME_TYPES the browser can record, or undefined to leave the
 * choice to the browser (no MediaRecorder, no isTypeSupported, or nothing matched).
 */
export const pickRecorderMimeType = (
  recorder: RecorderSupport | undefined = typeof MediaRecorder === 'undefined' ? undefined : MediaRecorder,
): string | undefined => {
  if (typeof recorder?.isTypeSupported !== 'function') {
    return undefined
  }

  return PREFERRED_RECORDER_MIME_TYPES.find((mimeType) => {
    try {
      return recorder.isTypeSupported(mimeType)
    } catch {
      return false
    }
  })
}

/**
 * Builds a recorder for `mimeType` when one was picked. isTypeSupported can over-report, so a
 * constructor that rejects the option falls back to the browser default instead of failing the
 * recording outright.
 */
export const createMediaRecorder = (stream: MediaStream, mimeType: string | undefined): MediaRecorder => {
  if (mimeType) {
    try {
      return new MediaRecorder(stream, { mimeType })
    } catch (error) {
      console.warn(`MediaRecorder rejected ${mimeType}, using the browser default`, error)
    }
  }

  return new MediaRecorder(stream)
}

/** 'audio/mp4; codecs=mp4a.40.2' -> 'audio/mp4'. Empty input gives ''. */
export const toBareMimeType = (mimeType?: string | null): string => {
  const [bareMimeType = ''] = (mimeType ?? '').split(';')

  return bareMimeType.trim().toLowerCase()
}

/**
 * Upload filename for a bare MIME type, or null when the backend cannot transcribe it. The
 * extension has to match the bytes: the transcription service picks its decoder from it.
 */
export const audioUploadFileName = (bareMimeType: string): string | null => {
  const extension = AUDIO_UPLOAD_EXTENSIONS.get(bareMimeType)

  return extension ? `recording.${extension}` : null
}

const HTTP_FORBIDDEN = 403

export type VoiceUploadErrorKey = 'verificationFailed' | 'noSpeechDetected' | 'uploadFailed'

interface VoiceUploadResult {
  ok: boolean
  data?: { question?: string; statusCode?: number } | null
}

/**
 * Which `voiceText.*` message a speech-to-text response calls for, or null on success. Kept in one
 * place so the widget and the dashboard test chatbot report failures identically.
 */
export const voiceUploadErrorKey = ({ ok, data }: VoiceUploadResult): VoiceUploadErrorKey | null => {
  if (!ok) {
    // The reCAPTCHA guard rejects before the audio is read: a verification problem, not a recording one.
    return data?.statusCode === HTTP_FORBIDDEN ? 'verificationFailed' : 'uploadFailed'
  }

  if (typeof data?.question !== 'string') {
    return 'uploadFailed'
  }

  // Transcribed, but nothing was said: silence, or a clip too short to hold speech.
  return data.question.trim() ? null : 'noSpeechDetected'
}
